import { useId, useRef, useState, type ChangeEvent } from 'react';
import { FileText, ImageIcon, Paperclip, X } from 'lucide-react';

import { formatFileSize } from '@/lib/format';
import { cn } from '@/lib/utils';
import { FieldNote } from './FieldNote';

export interface AttachmentPickerProps {
  label: string;
  hideLabel?: boolean;
  compact?: boolean;
  files: File[];
  onChange: (files: File[]) => void;
  /** MIME types accepted — checked here for fast feedback; the server decides. */
  acceptedTypes: readonly string[];
  /** What to call those types to a person: "PDF, PNG, JPG or WEBP". */
  acceptedLabel: string;
  maxFiles: number;
  maxFileMb: number;
  /** Ceiling on all the files together, when the destination has one. */
  maxTotalMb?: number;
  hint?: string;
  /** An error from the server, shown in place of the picker's own. */
  error?: string;
  disabled?: boolean;
}

const MB = 1024 * 1024;

/**
 * Several files, chosen one batch at a time and listed beneath.
 *
 * `FileUpload` holds exactly one file, which is right for a logo or a
 * worksheet. This is for a message or a report that carries a handful, where
 * each one needs its own remove button and the reader needs to see the total
 * against the limit before pressing Send.
 *
 * Files the picker can already tell the server will refuse — wrong type, too
 * large, one too many — are turned away here with a reason, and the rest of the
 * batch is kept.
 */
export function AttachmentPicker({
  label,
  hideLabel = false,
  compact = false,
  files,
  onChange,
  acceptedTypes,
  acceptedLabel,
  maxFiles,
  maxFileMb,
  maxTotalMb,
  hint,
  error,
  disabled = false,
}: AttachmentPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [rejection, setRejection] = useState<string | null>(null);

  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const isFull = files.length >= maxFiles;

  function handleSelect(event: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(event.target.files ?? []);
    // Cleared so choosing the same file again — after removing it — fires
    // `change` a second time.
    event.target.value = '';

    const accepted: File[] = [];
    let reason: string | null = null;
    let running = totalBytes;

    for (const file of picked) {
      if (files.length + accepted.length >= maxFiles) {
        reason = `You can attach up to ${maxFiles} files`;
        break;
      }
      if (!acceptedTypes.includes(file.type)) {
        reason = `${file.name} is not a ${acceptedLabel} file`;
        continue;
      }
      if (file.size > maxFileMb * MB) {
        reason = `${file.name} is larger than ${maxFileMb} MB`;
        continue;
      }
      if (maxTotalMb !== undefined && running + file.size > maxTotalMb * MB) {
        reason = `Attachments must add up to ${maxTotalMb} MB or less`;
        continue;
      }
      running += file.size;
      accepted.push(file);
    }

    setRejection(reason);
    if (accepted.length > 0) onChange([...files, ...accepted]);
  }

  function remove(index: number) {
    setRejection(null);
    onChange(files.filter((_, position) => position !== index));
  }

  const shownError = error ?? rejection ?? undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', compact && 'gap-1')}>
      <span id={`${id}-label`} className={cn('lbl', (hideLabel || compact) && 'sr-only')}>
        {label}
      </span>

      {files.length > 0 && (
        <ul
          className={cn(
            compact ? 'flex flex-wrap gap-1.5' : 'flex flex-col gap-1.5',
          )}
          aria-labelledby={`${id}-label`}
        >
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className={cn(
                compact
                  ? 'inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-2 py-1 text-xs text-ink-2 shadow-2xs'
                  : 'flex items-center gap-2.5 rounded-field border border-line bg-surface-2 px-3 py-2',
              )}
            >
              <span
                className={cn(
                  'shrink-0 place-items-center rounded-field text-accent',
                  compact ? 'grid h-4 w-4' : 'grid h-7 w-7 bg-accent-soft',
                )}
              >
                {file.type === 'application/pdf' ? (
                  <FileText className={compact ? 'h-3.5 w-3.5 text-red-500' : 'h-3.5 w-3.5'} aria-hidden />
                ) : (
                  <ImageIcon className={compact ? 'h-3.5 w-3.5 text-blue-500' : 'h-3.5 w-3.5'} aria-hidden />
                )}
              </span>
              <span className={cn('min-w-0 flex-1', compact && 'flex items-center gap-1.5')}>
                <span className={cn('block truncate text-ink', compact ? 'max-w-[130px] text-xs font-medium' : 'text-sm')}>
                  {file.name}
                </span>
                <span className={cn('block text-ink-3 tabular-nums', compact ? 'text-[10px]' : 'text-xs')}>
                  {formatFileSize(file.size)}
                </span>
              </span>
              <button
                type="button"
                onClick={() => remove(index)}
                disabled={disabled}
                aria-label={`Remove ${file.name}`}
                className={cn(
                  'shrink-0 text-ink-3 transition-colors hover:text-crit focus:outline-none cursor-pointer',
                  compact ? 'p-0.5 hover:bg-surface-2 rounded' : 'grid h-8 w-8 place-items-center rounded-field hover:bg-surface',
                )}
              >
                <X className={compact ? 'h-3 w-3' : 'h-4 w-4'} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className={cn('flex flex-wrap items-center justify-between gap-2', compact && 'gap-1.5')}>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || isFull}
          aria-describedby={shownError ? errorId : hint ? hintId : undefined}
          className={cn(
            compact
              ? 'inline-flex items-center gap-1 rounded-lg border border-line-strong bg-surface px-2.5 py-1 text-xs font-semibold text-ink-2 hover:border-accent hover:text-accent cursor-pointer'
              : 'inline-flex items-center gap-1.5 rounded-field border border-dashed border-line-strong bg-surface px-3 py-2 text-sm font-[560] text-ink-2 hover:border-accent hover:text-accent',
            'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line',
            'disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:border-line-strong disabled:hover:text-ink-2',
          )}
        >
          <Paperclip className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden />
          <span>{files.length === 0 ? 'Attach files' : 'Attach more'}</span>
        </button>
        {files.length > 0 && (
          <span className="text-xs tabular-nums text-ink-3">
            {files.length} of {maxFiles} · {formatFileSize(totalBytes)}
            {maxTotalMb !== undefined && ` of ${maxTotalMb} MB`}
          </span>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={acceptedTypes.join(',')}
        onChange={handleSelect}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
      />

      <FieldNote error={shownError} hint={hint} errorId={errorId} hintId={hintId} />
    </div>
  );
}
